import { useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { enqueueSnackbar } from "notistack"
import { faFileArrowDown, faPaperPlane, faPowerOff, faUserGroup } from "@fortawesome/pro-light-svg-icons"

import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { ErrorDisplay, SuccessDisplay } from "../../../../../../core/api/errorTypes"
import {
    ActionBarItem,
    ActionBarMenu,
    ActionBarMenuItem,
    FloatingActionBar,
} from "../../../../../../shared/components/table/FloatingActionBar"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { CompaniesUsersItem } from "../../../../../users/userTypes"

import { CompanyAdminUsersQuery, moveUsersToUserGroup, sendUsersInvite, updateUsersStatus } from "../usersApi"
import { useExportUsers } from "../hooks/useExportUsers"

type SelectOption = Readonly<{
    id: number
    name: string
}>

type UsersBulkActionBarProps = Readonly<{
    selectedUsers: ReadonlyArray<CompaniesUsersItem>
    userGroups: ReadonlyArray<SelectOption>
    onClose: () => void
}>

type BulkAction = "activate" | "deactivate" | "resend" | "move"

export function UsersBulkActionBar({ selectedUsers, userGroups, onClose }: UsersBulkActionBarProps) {
    const { l10n } = useAppLocalization()
    const queryClient = useQueryClient()
    const exportUsers = useExportUsers({ data: selectedUsers })

    const [pendingAction, setPendingAction] = useState<BulkAction | null>(null)

    const { mutateAsync: moveUsers } = useApiMutation(moveUsersToUserGroup, {
        successDisplay: SuccessDisplay.NONE,
        errorDisplay: ErrorDisplay.SNACKBAR,
    })
    const { mutateAsync: setUsersStatus } = useApiMutation(updateUsersStatus, {
        successDisplay: SuccessDisplay.NONE,
        errorDisplay: ErrorDisplay.SNACKBAR,
    })
    const { mutateAsync: inviteUsers } = useApiMutation(sendUsersInvite, {
        successDisplay: SuccessDisplay.NONE,
        errorDisplay: ErrorDisplay.SNACKBAR,
    })

    const userIds = selectedUsers.map((user) => user.id)

    // The move/status/invite endpoints are atomic (one request for the whole selection), so we don't
    // fan out — on failure `errorDisplay: SNACKBAR` already surfaces the backend message.
    async function runAtomic(action: BulkAction, call: () => Promise<unknown>) {
        setPendingAction(action)
        try {
            await call()
            await queryClient.invalidateQueries({ queryKey: [CompanyAdminUsersQuery.USERS_FOR_COMPANY] })
            enqueueSnackbar(l10n.getString("company-users-bulk-success", { count: selectedUsers.length }), {
                variant: "success",
            })
            onClose()
        } catch {
            // errorDisplay: SNACKBAR already surfaced the failure.
        } finally {
            setPendingAction(null)
        }
    }

    const setActive = (isActive: boolean) =>
        runAtomic(isActive ? "activate" : "deactivate", () => setUsersStatus({ isActive, userIds }))

    const resendInvitations = () => runAtomic("resend", () => inviteUsers({ userIds }))

    const moveToGroup = (groupId: number) => runAtomic("move", () => moveUsers({ userGroupId: groupId, userIds }))

    return (
        <FloatingActionBar selectedItemsLength={selectedUsers.length} handleCloseActionBar={onClose}>
            <LocalizedStrict id="company-users-bulk-move-group" attrs={{ title: true }}>
                <ActionBarMenu icon={faUserGroup} title="Move to group" isLoading={pendingAction === "move"}>
                    {userGroups.map((group) => (
                        <ActionBarMenuItem key={group.id} title={group.name} onClick={() => moveToGroup(group.id)} />
                    ))}
                </ActionBarMenu>
            </LocalizedStrict>
            <LocalizedStrict id="company-users-bulk-status" attrs={{ title: true }}>
                <ActionBarMenu
                    icon={faPowerOff}
                    title="Status"
                    isLoading={pendingAction === "activate" || pendingAction === "deactivate"}
                >
                    <LocalizedStrict id="company-users-bulk-set-active" attrs={{ title: true }}>
                        <ActionBarMenuItem title="Active" onClick={() => setActive(true)} />
                    </LocalizedStrict>
                    <LocalizedStrict id="company-users-bulk-set-inactive" attrs={{ title: true }}>
                        <ActionBarMenuItem title="Inactive" onClick={() => setActive(false)} />
                    </LocalizedStrict>
                </ActionBarMenu>
            </LocalizedStrict>
            <LocalizedStrict id="company-users-bulk-resend-invite" attrs={{ title: true }}>
                <ActionBarItem
                    icon={faPaperPlane}
                    title="Resend invite"
                    isLoading={pendingAction === "resend"}
                    onClick={resendInvitations}
                />
            </LocalizedStrict>
            <LocalizedStrict id="company-users-bulk-export" attrs={{ title: true }}>
                <ActionBarItem icon={faFileArrowDown} title="Export" onClick={exportUsers} />
            </LocalizedStrict>
        </FloatingActionBar>
    )
}
